import React from 'react';
import { Chip } from '@mui/material';
import { API_URL } from '../../Utils/url';

export const FONT = 'Mulish, sans-serif';

export const authToken = () => {
  const raw = sessionStorage.getItem('token') || localStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};

export const img = (p) => (p ? (p.startsWith('http') || p.startsWith('blob:') || p.startsWith('data:') ? p : `${API_URL.replace(/\/api\/?$/, '/')}${p.replace(/^\//, '')}`) : undefined);
export const inr = (v) => (v === undefined || v === null || v === '' ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
export const isVideoUrl = (u) => typeof u === 'string' && /\.(mp4|webm|ogg|mov|avi|mkv)(\?|$)/i.test(u);

export const yellowBtn = { textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' }, '&.Mui-disabled': { background: '#f1f5f9', color: '#94a3b8', boxShadow: 'none' } };

export const UNIT_NAMES = ['ml', 'L', 'g', 'kg', 'pouch', 'packet', 'sachet', 'unit'];
export const CONTAINERS = ['bottle', 'box', 'packet', 'can', 'jar', 'bag', 'pouch', 'tube', 'roll', 'crate', 'sachet', 'drum', 'blister_pack', 'strip', 'unit',
  // beauty / salon packaging
  'tub', 'pump_bottle', 'spray_bottle', 'dropper_bottle', 'ampoule', 'vial', 'stick', 'roll_on', 'compact', 'kit', 'carton', 'bucket', 'tin', 'canister', 'bar', 'sheet'];
export const IDEAL_FOR = ['men', 'women', 'unisex', 'kids'];
export const GST_RATES = ['5', '12', '18', '28'];
export const BADGES = ['New Arrival', 'Best Seller', "Editor's Choice", 'Limited Edition', 'Top Rated', 'Hot Deal', 'Organic'];
export const SKIN_TYPES = ['All Skin Types', 'Oily', 'Dry', 'Combination', 'Sensitive', 'Normal', 'Acne Prone'];
export const HAIR_TYPES = ['All Hair Types', 'Oily', 'Dry', 'Normal', 'Color Treated', 'Curly', 'Straight', 'Thinning'];
export const SHIPPING_CLASSES = ['Standard Shipping', 'Express Shipping', 'Next Day Delivery', 'Free Shipping', 'Heavy Item'];

export const STATUS = {
  approved: { label: 'Approved', bg: '#dcfce7', fg: '#166534' },
  pending: { label: 'Pending', bg: '#fef3c7', fg: '#92400e' },
  rejected: { label: 'Rejected', bg: '#fee2e2', fg: '#991b1b' },
};
export const StatusChip = ({ status }) => {
  const s = STATUS[status] || { label: status || 'Unknown', bg: '#f1f5f9', fg: '#475569' };
  return <Chip size="small" label={s.label} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: s.bg, color: s.fg }} />;
};

// stock label from the live counters
export const stockOf = (p) => {
  const qty = Number(p?.availableQuantity) || 0;
  const min = Number(p?.minimumStockQuantity) || 0;
  // out of stock when 0, or down to 25% of the minimum stock quantity (seller products never run out)
  const out = p?.ownership !== 'Seller Product' && (qty <= 0 || (min > 0 && qty <= min * 0.25));
  if (out) return { key: 'out', label: 'Out of stock', bg: '#fee2e2', fg: '#991b1b' };
  if (qty > 0 && qty <= min) return { key: 'low', label: `Low · ${qty}`, bg: '#fef3c7', fg: '#92400e' };
  return { key: 'in', label: `${qty} in stock`, bg: '#dcfce7', fg: '#166534' };
};

export const initialFormState = {
  title: '', description: '', key_features: '', how_to_use: '', category_id: '', subcategory_id: '', upc: '',
  product_images: [], productVideos: [], unitType: '', unitValue: 0, unitName: '', status: 'approved', isPublished: false,
  brand: '', ideal_for: '', applied_for: '', container_type: '', country_of_orgin: '', color: '', gst: '', hsn_code: '',
  codAvailable: false, tags: [], b2c_mrp: '', b2c_discountType: '', b2c_discountValue: '', b2c_sellingPrice: '',
  b2b_discountType: '', b2b_discountValue: '', b2b_sellingPrice: '', visibility: ['b2b', 'b2c'], isFree: false,
  thresholdAmount: '', autoAdd: false, minimumStockQuantity: '', length: '', width: '', height: '', dimensionUnit: '',
  weight: '', weightUnit: '', slug: '', meta_title: '', meta_description: '', badge: '', is_featured: false,
  ingredients: '', skin_type: '', hair_type: '', fragrance_notes: '', is_vegan: false, cruelty_free: false, organic: false,
  shipping_class: '', return_policy: 0, manufacturerName: '', manufacturerAddress: '', manufacturingLicenseNumber: '',
};

const price = (mrp, type, value) => {
  const m = parseFloat(mrp) || 0;
  const d = parseFloat(value) || 0;
  if (type === 'flat') return Math.max(0, m - d).toFixed(0);
  if (type === 'percentage') return Math.max(0, m - (m * Math.min(100, d)) / 100).toFixed(0);
  return m.toFixed(0);
};

// single place that applies a field change and keeps the derived prices in step
export const applyFormChange = (form, name, value) => {
  const next = { ...form, [name]: value };
  if (['b2c_mrp', 'b2c_discountType', 'b2c_discountValue'].includes(name)) next.b2c_sellingPrice = price(next.b2c_mrp, next.b2c_discountType, next.b2c_discountValue);
  if (['b2c_mrp', 'b2b_discountType', 'b2b_discountValue'].includes(name)) next.b2b_sellingPrice = price(next.b2c_mrp, next.b2b_discountType, next.b2b_discountValue);
  if (name === 'category_id') next.subcategory_id = '';
  return next;
};

// turn a product from the API into form state
export const formFromProduct = (p) => {
  let dims = p.dimensions;
  if (typeof dims === 'string') { try { dims = JSON.parse(dims); } catch { dims = {}; } }
  const list = (v, fallback) => (Array.isArray(v) ? v : typeof v === 'string' ? v.split(',').map((x) => x.trim()).filter(Boolean) : fallback);
  const f = {
    ...initialFormState,
    ...p,
    category_id: p.category_id?._id || p.category_id || '',
    subcategory_id: p.subcategory_id?._id || p.subcategory_id || '',
    supplier_id: p.supplier_id?._id || p.supplier_id || undefined,
    product_images: [...(Array.isArray(p.product_images) ? p.product_images : []), ...(Array.isArray(p.productVideos) ? p.productVideos : [])],
    tags: list(p.tags, []),
    visibility: list(p.visibility, ['b2b', 'b2c']),
    length: dims?.length || '', width: dims?.width || '', height: dims?.height || '',
    dimensionUnit: dims?.unit || '', weight: dims?.weight || '', weightUnit: dims?.weightUnit || '',
  };
  Object.keys(f).forEach((k) => { if (f[k] === null) f[k] = ''; });
  return f;
};

const NUMERIC = ['b2c_mrp', 'b2c_discountValue', 'b2c_sellingPrice', 'b2b_discountValue', 'b2b_sellingPrice', 'thresholdAmount', 'minimumStockQuantity', 'return_policy', 'unitValue'];
const SKIP = ['_id', '__v', 'createdAt', 'updatedAt', 'createdBy', 'seller_id', 'supplier_id', 'gsin', 'productCode', 'availableQuantity', 'inStock', 'stockStatus', 'ownership', 'isDeleted', 'productVideos', 'productVideo', 'dimensions', 'length', 'width', 'height', 'dimensionUnit', 'weight', 'weightUnit', 'product_images', 'tags', 'visibility', 'requestedStatus'];

// the multipart body the product endpoints expect
export const buildProductFormData = (form, { isEdit = false } = {}) => {
  const body = new FormData();
  Object.entries(form).forEach(([k, v]) => {
    if (SKIP.includes(k) || v === undefined || v === null) return;
    if (NUMERIC.includes(k)) {
      if (v === '' && isEdit) return;
      body.append(k, v === '' ? 0 : Number(v));
    } else body.append(k, v);
  });
  const media = Array.isArray(form.product_images) ? form.product_images : [];
  if (media.length === 0) { body.append('product_images', ''); body.append('productVideos', ''); }
  media.forEach((m) => {
    if (m instanceof File) body.append(m.type.startsWith('video/') ? 'productVideos' : 'product_images', m);
    else if (typeof m === 'string' && m.trim()) body.append(isVideoUrl(m) ? 'productVideos' : 'product_images', m);
  });
  if (Array.isArray(form.tags)) body.append('tags', form.tags.join(','));
  if (Array.isArray(form.visibility)) body.append('visibility', form.visibility.join(','));
  const dimensions = {
    length: form.length ? Number(form.length) : undefined, width: form.width ? Number(form.width) : undefined, height: form.height ? Number(form.height) : undefined,
    unit: form.dimensionUnit || undefined, weight: form.weight ? Number(form.weight) : undefined, weightUnit: form.weightUnit || undefined,
  };
  Object.keys(dimensions).forEach((k) => dimensions[k] === undefined && delete dimensions[k]);
  if (Object.keys(dimensions).length) body.set('dimensions', JSON.stringify(dimensions));
  return body;
};

export const isRichEmpty = (html) => {
  if (!html) return true;
  const text = html.replace(/<[^>]*>?/gm, '').replace(/&nbsp;/g, '').trim();
  return text === '' && !html.includes('<img');
};

// whole-form validation; returns { field: message }
export const validateProduct = (f) => {
  const e = {};
  if (!String(f.title || '').trim()) e.title = 'Give the product a title';
  if (!String(f.description || '').trim()) e.description = 'Add a short description';
  if (!f.category_id) e.category_id = 'Choose a category';
  if (!f.subcategory_id) e.subcategory_id = 'Choose a subcategory';
  if (!String(f.brand || '').trim()) e.brand = 'Choose or type a brand';
  if (!f.product_images || f.product_images.length === 0 || !f.product_images.some((m) => !(m instanceof File && m.type.startsWith('video/')) && !isVideoUrl(m))) e.product_images = 'Add at least one image';
  if (isRichEmpty(f.key_features)) e.key_features = 'Add the key features';
  if (isRichEmpty(f.how_to_use)) e.how_to_use = 'Add how to use it';
  if (!f.unitType) e.unitType = 'Choose a unit type';
  if (!(Number(f.unitValue) > 0)) e.unitValue = 'Enter the pack size';
  if (f.unitType === 'weight' && !f.unitName) e.unitName = 'Choose the unit';
  if (!f.gst) e.gst = 'Choose the GST rate';
  const vis = Array.isArray(f.visibility) ? f.visibility : [];
  if (vis.length === 0) e.visibility = 'Choose B2C, B2B or both';
  if (f.isFree) {
    if (!(Number(f.thresholdAmount) > 0)) e.thresholdAmount = 'Enter the cart amount that unlocks it';
  } else {
    const mrp = Number(f.b2c_mrp);
    if (!(mrp > 0)) e.b2c_mrp = 'Enter the MRP';
    if (vis.includes('b2c')) {
      if (!(Number(f.b2c_sellingPrice) > 0)) e.b2c_sellingPrice = 'B2C price is required';
      else if (mrp && Number(f.b2c_sellingPrice) > mrp) e.b2c_sellingPrice = 'Cannot be higher than the MRP';
    }
    if (vis.includes('b2b')) {
      if (!(Number(f.b2b_sellingPrice) > 0)) e.b2b_sellingPrice = 'B2B price is required';
      else if (mrp && Number(f.b2b_sellingPrice) > mrp) e.b2b_sellingPrice = 'Cannot be higher than the MRP';
    }
    if (f.b2c_discountType === 'percentage' && Number(f.b2c_discountValue) > 100) e.b2c_discountValue = 'At most 100%';
    if (f.b2b_discountType === 'percentage' && Number(f.b2b_discountValue) > 100) e.b2b_discountValue = 'At most 100%';
  }
  if (!f.ideal_for) e.ideal_for = 'Choose who it is for';
  if (!f.container_type) e.container_type = 'Choose the container';
  if (f.slug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(f.slug)) e.slug = 'Lowercase letters, numbers and dashes only';
  if (Number(f.return_policy) < 0) e.return_policy = 'Cannot be negative';
  return e;
};

// which form section each error belongs to, for the side navigation
export const SECTION_OF = {
  title: 'basics', description: 'basics', category_id: 'basics', subcategory_id: 'basics', brand: 'basics', visibility: 'basics',
  product_images: 'media',
  key_features: 'details', how_to_use: 'details', unitType: 'details', unitValue: 'details', unitName: 'details', ideal_for: 'details', container_type: 'details',
  gst: 'pricing', b2c_mrp: 'pricing', b2c_sellingPrice: 'pricing', b2b_sellingPrice: 'pricing', thresholdAmount: 'pricing', b2c_discountValue: 'pricing', b2b_discountValue: 'pricing',
  return_policy: 'logistics', slug: 'seo',
};

// Rich text written by sellers is shown to admins, so strip anything executable before rendering it.
const SCRIPT_PREFIX = ['javascript', ':'].join('');
export const safeHtml = (html) => {
  if (!html || typeof DOMParser === 'undefined') return '';
  const doc = new DOMParser().parseFromString(String(html), 'text/html');
  doc.querySelectorAll('script,style,iframe,object,embed,link,meta,base,form').forEach((n) => n.remove());
  doc.body.querySelectorAll('*').forEach((el) => {
    [...el.attributes].forEach((a) => {
      const name = a.name.toLowerCase();
      const val = String(a.value).trim().toLowerCase();
      if (name.startsWith('on') || ((name === 'href' || name === 'src' || name === 'xlink:href') && (val.startsWith(SCRIPT_PREFIX) || val.startsWith('data:text/html')))) el.removeAttribute(a.name);
    });
  });
  return doc.body.innerHTML;
};
